import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Link2, Clock, Users, Fingerprint, Smartphone,
  AlertTriangle, RefreshCw, Gauge, FileCheck2, Activity, KeyRound,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { KPI } from "@/components/admin/adminShared";
import TriageCenter from "@/components/security/TriageCenter";

const MODE_COLORS = {
  monitor: "bg-amber-100 text-amber-700",
  grace: "bg-amber-100 text-amber-700",
  enforce: "bg-green-100 text-green-700",
  off: "bg-slate-100 text-slate-500",
};
const RISK_COLORS = {
  low: "bg-green-100 text-green-700", medium: "bg-amber-100 text-amber-700",
  high: "bg-orange-100 text-orange-700", critical: "bg-red-100 text-red-700",
  unknown: "bg-slate-100 text-slate-500",
};

function Section({ title, icon: Icon, children, testid, action }) {
  return (
    <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 font-heading font-semibold text-[#0A0F1C]">
          <Icon size={17} className="text-[#2563EB]" /> {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Pill({ children, cls }) {
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{children}</span>;
}

export default function SecurityDashboard() {
  const [ov, setOv] = useState(null);
  const [events, setEvents] = useState(null);
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/security/dashboard/overview"),
      api.get("/admin/security/dashboard/monitor-events", { params: { limit: 25 } }),
      api.get("/admin/security/dashboard/devices", { params: { limit: 15 } }),
    ]).then(([o, e, d]) => { setOv(o.data); setEvents(e.data); setDevices(d.data); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const ap = ov?.approvals || {};
  const me = ov?.monitor_events || {};
  const enf = ov?.enforcement || {};
  const states = ov?.verification_states || {};

  return (
    <AppShell title="Security Dashboard" subtitle="Read-only operational view of platform security controls (S1.2–S1.6).">
      <div className="space-y-5" data-testid="security-dashboard">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap" data-testid="security-modes">
            <span className="text-xs uppercase tracking-wider text-slate-400">Operating modes</span>
            <span className="flex items-center gap-1.5 text-sm">Rate-limit <Pill cls={MODE_COLORS[enf.rate_limit_mode]}>{enf.rate_limit_mode || "—"}</Pill></span>
            <span className="flex items-center gap-1.5 text-sm">CSRF <Pill cls={MODE_COLORS[enf.csrf_mode]}>{enf.csrf_mode || "—"}</Pill></span>
            <span className="flex items-center gap-1.5 text-sm">MFA <Pill cls={MODE_COLORS[enf.mfa_mode]}>{enf.mfa_mode || "—"}</Pill></span>
          </div>
          <button onClick={load} data-testid="security-refresh" className="h-9 px-3 rounded-md border border-slate-200 text-sm text-slate-600 flex items-center gap-1.5 hover:bg-slate-50">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <a href="/incident-response" data-testid="link-incident-response" className="h-9 px-3 rounded-md bg-red-600 text-white text-sm flex items-center gap-1.5 hover:bg-red-700">
            <ShieldAlert size={14} /> Incident Response
          </a>
        </div>
        <div className="text-xs text-slate-400 -mt-2">{enf.note}</div>

        <ReadinessSection />

        <TriageCenter />


        {ov?.audit_chain?.any_broken && (
          <div className="border border-red-200 bg-red-50 rounded-lg p-4 flex items-start gap-3" data-testid="broken-chain-alert">
            <AlertTriangle className="text-red-600 shrink-0" size={20} />
            <div>
              <div className="font-semibold text-red-700">Broken-chain alert — {ov.audit_chain.broken_chain_alerts.length} stream(s) failed verification</div>
              <ul className="text-xs text-red-600 mt-1 space-y-0.5">
                {ov.audit_chain.broken_chain_alerts.map((a, i) => (
                  <li key={i}>{a.stream} — {a.failure_category} @ seq {a.first_failing_sequence}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KPI icon={Clock} label="Pending Approval" value={ap.pending ?? "—"} sub={`oldest ${ap.oldest_pending_age_hours ?? 0}h`} accent="text-amber-500" />
          <KPI icon={FileCheck2} label="Verified" value={ov?.verified_accounts ?? "—"} sub={`${ap.approval_rate_pct ?? 0}% approval rate`} accent="text-green-500" />
          <KPI icon={KeyRound} label="MFA Enrolled" value={`${ov?.mfa_enrollment?.enrollment_percentage ?? 0}%`} sub={`${ov?.mfa_enrollment?.enrolled ?? 0}/${ov?.mfa_enrollment?.total_enforced_accounts ?? 0} staff`} accent="text-blue-500" />
          <KPI icon={Link2} label="Chain Streams" value={ov?.audit_chain?.total_streams ?? "—"} sub={ov?.audit_chain?.any_broken ? "broken!" : "all intact"} accent={ov?.audit_chain?.any_broken ? "text-red-500" : "text-green-500"} />
          <KPI icon={Smartphone} label="New-Device 7d" value={ov?.new_device_alerts_7d ?? "—"} sub={`${ov?.trusted_devices_active ?? 0} trusted`} accent="text-purple-500" />
          <KPI icon={Gauge} label="Would-Block 7d" value={(me.would_block_if_enforced?.rate_limit_7d ?? 0) + (me.would_block_if_enforced?.csrf_7d ?? 0)} sub="if enforced" accent="text-orange-500" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Approval metrics */}
          <Section title="Approval Queue & Reviewer SLA" icon={ShieldCheck} testid="section-approvals">
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <Metric label="Avg pending age" value={`${ap.avg_pending_age_hours ?? 0}h`} />
              <Metric label="Oldest pending" value={`${ap.oldest_pending_age_hours ?? 0}h`} />
              <Metric label="Hard-stop rate" value={`${ap.hard_stop_rate_pct ?? 0}%`} />
              <Metric label="Rejection rate" value={`${ap.rejection_rate_pct ?? 0}%`} />
              <Metric label="Restricted/Suspended rate" value={`${ap.restricted_rate_pct ?? 0}%`} />
              <Metric label="Total decisions" value={ap.decisions_total ?? 0} />
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">TradeGuard risk breakdown</div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="risk-breakdown">
              {Object.entries(ap.risk_breakdown || {}).map(([lvl, n]) => (
                <Pill key={lvl} cls={RISK_COLORS[lvl] || RISK_COLORS.unknown}>{lvl}: {n}</Pill>
              ))}
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Reviewer workload</div>
            <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="reviewer-workload">
              {(ap.reviewer_workload || []).length === 0 ? <div className="text-xs text-slate-300">No decisions yet.</div> :
                (ap.reviewer_workload || []).map((r) => (
                  <div key={r.reviewer_user_id} className="flex justify-between text-xs">
                    <span className="text-slate-500 truncate">{r.reviewer_user_id}</span>
                    <span className="font-mono text-slate-700">{r.decisions} cases</span>
                  </div>
                ))}
            </div>
          </Section>

          {/* Verification states */}
          <Section title="Verification States" icon={Users} testid="section-verification-states">
            <div className="space-y-2">
              {Object.entries(states).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
                <div key={st} className="flex items-center justify-between text-sm" data-testid={`vstate-${st}`}>
                  <span className="text-slate-600 capitalize">{st.replace(/_/g, " ")}</span>
                  <span className="font-mono font-semibold text-[#0A0F1C]">{n}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Monitor events */}
          <Section title="Rate-Limit & CSRF Monitor" icon={Activity} testid="section-monitor">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="border border-slate-100 rounded-md p-3">
                <div className="text-xs text-slate-400">Rate-limit signals</div>
                <div className="text-sm mt-1">24h <b>{me.rate_limit?.last_24h ?? 0}</b> · 7d <b>{me.rate_limit?.last_7d ?? 0}</b> · total <b>{me.rate_limit?.total ?? 0}</b></div>
              </div>
              <div className="border border-slate-100 rounded-md p-3">
                <div className="text-xs text-slate-400">CSRF signals</div>
                <div className="text-sm mt-1">24h <b>{me.csrf?.last_24h ?? 0}</b> · 7d <b>{me.csrf?.last_7d ?? 0}</b> · total <b>{me.csrf?.total ?? 0}</b></div>
              </div>
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Recent events</div>
            <div className="space-y-1 max-h-40 overflow-y-auto text-xs" data-testid="monitor-events-list">
              {(events?.events || []).length === 0 ? <div className="text-slate-300">No monitor events.</div> :
                (events?.events || []).slice(0, 15).map((e, i) => (
                  <div key={i} className="flex justify-between border-b border-slate-50 pb-1">
                    <span className="text-slate-600">{e.event_type}</span>
                    <span className="text-slate-400">{(e.created_at || "").slice(0, 16)}</span>
                  </div>
                ))}
            </div>
          </Section>

          {/* Devices */}
          <Section title="Trusted & New-Device Activity" icon={Fingerprint} testid="section-devices">
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <Metric label="Trusted (active)" value={devices?.trusted_devices?.active ?? 0} />
              <Metric label="Trusted (expired)" value={devices?.trusted_devices?.expired ?? 0} />
              <Metric label="New-device 7d" value={devices?.new_device_alerts?.last_7d ?? 0} />
            </div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">New-device login alerts</div>
            <div className="space-y-1 max-h-32 overflow-y-auto text-xs" data-testid="new-device-list">
              {(devices?.new_device_alerts?.recent || []).length === 0 ? <div className="text-slate-300">No new-device alerts.</div> :
                (devices?.new_device_alerts?.recent || []).map((e, i) => (
                  <div key={i} className="flex justify-between border-b border-slate-50 pb-1">
                    <span className="text-slate-600 truncate">{e.detail?.device || e.user_id || "device"}</span>
                    <span className="text-slate-400">{(e.created_at || "").slice(0, 16)}</span>
                  </div>
                ))}
            </div>
          </Section>
        </div>

        {/* Chain health streams */}
        <Section title="Audit-Chain Health" icon={ShieldAlert} testid="section-chain-health">
          <div className="text-xs text-slate-400 mb-3">
            {ov?.audit_chain?.total_streams ?? 0} partitioned streams. Most-recent streams are live-verified on load; use the per-stream S1.5 verify API for older streams.
          </div>
          <ChainStreams />
        </Section>
      </div>
    </AppShell>
  );
}

const LIGHT = {
  not_ready: { emoji: "🔴", label: "Not Ready", cls: "border-red-300 bg-red-50 text-red-700" },
  ready_with_warnings: { emoji: "🟡", label: "Ready with Warnings", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  ready_for_enforcement: { emoji: "🟢", label: "Ready for Enforcement", cls: "border-green-300 bg-green-50 text-green-700" },
};
const CHECK_STATUS = {
  pass: "bg-green-100 text-green-700", warn: "bg-amber-100 text-amber-700", fail: "bg-red-100 text-red-700",
};

function ReadinessSection() {
  const [rd, setRd] = useState(null);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/admin/security/dashboard/readiness"),
      api.get("/admin/security/dashboard/enforcement-simulation", { params: { window_days: 30 } }),
    ]).then(([r, s]) => { setRd(r.data); setSim(s.data); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const attest = async (item, label) => {
    const note = window.prompt(`Attestation note for "${label}" (required):`, "");
    if (note === null) return;
    if (!note.trim()) return toast.error("A note is required to attest.");
    setBusy(true);
    try {
      await api.post("/admin/security/dashboard/readiness/attest", { item, done: true, note });
      toast.success(`Attested: ${label}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!rd) return null;
  const light = LIGHT[rd.overall_status] || LIGHT.not_ready;

  return (
    <div className="border border-slate-200 bg-white rounded-lg p-5 space-y-4" data-testid="readiness-section">
      <div className={`rounded-lg border p-4 flex items-center justify-between ${light.cls}`} data-testid="readiness-status">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{light.emoji}</span>
          <div>
            <div className="font-heading font-bold text-lg">Security Enforcement Readiness — {light.label}</div>
            <div className="text-xs opacity-80">Preview-only readiness assessment. No enforcement has been activated.</div>
          </div>
        </div>
        <button onClick={load} data-testid="readiness-refresh" className="h-9 px-3 rounded-md bg-white/70 border border-current/20 text-sm flex items-center gap-1.5">
          <RefreshCw size={14} /> Re-evaluate
        </button>
      </div>

      {rd.hard_blockers?.length > 0 && (
        <div className="text-xs text-red-600" data-testid="readiness-blockers">
          <b>{rd.hard_blockers.length} hard blocker(s):</b> {rd.hard_blockers.map((b) => b.label).join(", ")}
        </div>
      )}

      {/* Go / No-Go table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="readiness-table">
          <thead className="text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left font-semibold py-2">Prerequisite</th>
              <th className="text-left font-semibold py-2">Type</th>
              <th className="text-left font-semibold py-2">Current</th>
              <th className="text-left font-semibold py-2">Required</th>
              <th className="text-left font-semibold py-2">Status</th>
              <th className="text-left font-semibold py-2">Corrective Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rd.checks.map((c) => (
              <tr key={c.id} data-testid={`readiness-check-${c.id}`}>
                <td className="py-2 text-[#0A0F1C]">{c.label}</td>
                <td className="py-2 text-slate-400 text-xs capitalize">{c.severity === "warning" ? "warning" : c.category}</td>
                <td className="py-2 font-mono text-xs">{String(c.current_value)}</td>
                <td className="py-2 font-mono text-xs text-slate-400">{String(c.required_value)}</td>
                <td className="py-2">
                  <Pill cls={CHECK_STATUS[c.status]}>{c.status}</Pill>
                  {c.id.startsWith("attest_") && c.status !== "pass" && (
                    <button onClick={() => attest(c.id.replace("attest_", ""), c.label)} disabled={busy}
                      data-testid={`attest-${c.id.replace("attest_", "")}`}
                      className="ml-2 h-6 px-2 rounded text-[11px] bg-[#0A0F1C] text-white">Attest</button>
                  )}
                </td>
                <td className="py-2 text-xs text-slate-500">{c.corrective_action || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dry-run simulation */}
      {sim && (
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="readiness-simulation">
          <div className="flex items-center gap-2 font-semibold text-[#0A0F1C] mb-1">
            <Gauge size={16} className="text-orange-500" /> Dry-Run Enforcement Simulation ({sim.analysis_window_days}d)
          </div>
          <div className="text-[11px] text-orange-600 mb-3 font-medium">{sim.label}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Metric label="Est. blocked requests" value={sim.estimated_blocked_requests} />
            <Metric label="Est. users affected" value={sim.estimated_unique_users_affected} />
            <Metric label="Unreviewed critical" value={sim.unreviewed_critical_events} />
            <Metric label="Excluded test/demo" value={sim.excluded_test_demo_events} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {["rate_limit", "csrf"].map((ctrl) => (
              <div key={ctrl} className="border border-slate-100 bg-white rounded-md p-3" data-testid={`sim-${ctrl}`}>
                <div className="text-xs font-semibold uppercase text-slate-500 mb-1">{ctrl.replace("_", "-")}</div>
                <div className="text-xs text-slate-600">Blocked: <b>{sim[ctrl].estimated_blocked_requests}</b> · Users: <b>{sim[ctrl].estimated_unique_users_affected}</b> · FP est: <b>{sim[ctrl].false_positive_rate_estimate}%</b></div>
                <div className="text-xs text-slate-500 mt-1">Top causes: {sim[ctrl].top_causes.map((t) => `${t.cause}(${t.count})`).join(", ") || "none"}</div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-3">{sim.data_quality_warning}</div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border border-slate-100 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="font-mono text-lg font-bold text-[#0A0F1C]">{value}</div>
    </div>
  );
}

function ChainStreams() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    api.get("/admin/security/dashboard/chain-health", { params: { live_verify_limit: 25 } })
      .then(({ data }) => setRows(data.streams || []))
      .catch(() => {}).finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-slate-400">
          <tr>
            <th className="text-left font-semibold py-2">Stream</th>
            <th className="text-left font-semibold py-2">Category</th>
            <th className="text-left font-semibold py-2">Length</th>
            <th className="text-left font-semibold py-2">Head</th>
            <th className="text-left font-semibold py-2">Verification</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-testid="chain-streams">
          {rows.slice(0, 40).map((s) => (
            <tr key={s.stream} data-testid={`chain-row-${s.stream}`}>
              <td className="py-2 font-mono text-xs text-[#0A0F1C]">{s.stream}</td>
              <td className="py-2 text-slate-500 text-xs">{s.category}</td>
              <td className="py-2 font-mono text-xs">{s.length}</td>
              <td className="py-2 font-mono text-xs text-slate-400">{s.head_fingerprint}</td>
              <td className="py-2">
                {s.verification ? (
                  <Pill cls={s.verification.status === "failed" ? "bg-red-100 text-red-700" : s.verification.status === "verified" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                    {s.verification.status}{s.verification.failure_category ? ` · ${s.verification.failure_category}` : ""}
                  </Pill>
                ) : (
                  <span className="text-xs text-slate-400">{s.last_verification_status || "not verified"}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {busy && <div className="text-xs text-slate-400 py-2">Verifying streams…</div>}
    </div>
  );
}
